import {IProjekt} from '../../../../shared/model/projekt.model';
import {Aufgabe} from '../../../../shared/model/aufgabe.model';

type ProjectRecord = Record<string, unknown>;

export interface PendingTask {
  index: number;
  sent: Aufgabe;
}

const same = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);

export function rebaseProjectEdits(
  base: IProjekt,
  local: IProjekt,
  saved: IProjekt,
  pending: PendingTask | null,
  sentNewIndexes: number[] = []
): IProjekt {
  if (same(local, base)) return saved;
  const baseRecord = base as unknown as ProjectRecord;
  const localRecord = local as unknown as ProjectRecord;
  const savedRecord = saved as unknown as ProjectRecord;
  const result: ProjectRecord = {};
  const keys = new Set([...Object.keys(savedRecord), ...Object.keys(localRecord)]);
  keys.delete('tasks');
  for (const key of keys) {
    result[key] = same(localRecord[key], baseRecord[key]) ? savedRecord[key] : localRecord[key];
  }
  result['tasks'] = rebaseTasks(base.tasks ?? [], local.tasks ?? [], saved.tasks ?? [], pending, sentNewIndexes);
  return result as unknown as IProjekt;
}

function rebaseTasks(
  base: Aufgabe[],
  local: Aufgabe[],
  saved: Aufgabe[],
  pending: PendingTask | null,
  sentNewIndexes: number[]
): Aufgabe[] {
  const savedByUuid = (uuid: string | undefined) => saved.find(t => t.uuid === uuid);
  const sentNew = base.filter(t => !t.uuid);
  const savedNew = pending ? [] : appendedTasks(base, saved).slice(0, sentNew.length);
  return local.flatMap((task, idx) => {
    if (pending && idx === pending.index) {
      const savedTask = task.uuid ? savedByUuid(task.uuid) : appendedTask(base, saved);
      if (!savedTask) return [task];
      if (sameTask(task, pending.sent)) return [savedTask];
      return [task.uuid ? task : new Aufgabe(task.caption, task.details, task.skills, savedTask.uuid)];
    }
    if (!task.uuid) {
      const sentIdx = sentNewIndexes.indexOf(idx);
      const savedTask = sentIdx >= 0 ? savedNew[sentIdx] : undefined;
      const sentTask = sentNew[sentIdx];
      if (!savedTask) return [task];
      if (same(task, sentTask)) return [savedTask];
      return [new Aufgabe(task.caption, task.details, task.skills, savedTask.uuid)];
    }
    const baseTask = base.find(t => t.uuid === task.uuid);
    if (!baseTask || !same(task, baseTask)) return [task];
    const savedTask = savedByUuid(task.uuid);
    return savedTask ? [savedTask] : [];
  });
}

function sameTask(a: Aufgabe, b: Aufgabe): boolean {
  return same([a.caption, a.details, a.skills ?? [], a.uuid], [b.caption, b.details, b.skills ?? [], b.uuid]);
}

function appendedTask(base: Aufgabe[], saved: Aufgabe[]): Aufgabe | undefined {
  return appendedTasks(base, saved)[0];
}

function appendedTasks(base: Aufgabe[], saved: Aufgabe[]): Aufgabe[] {
  const known = new Set(base.map(t => t.uuid));
  return saved.filter(t => !!t.uuid && !known.has(t.uuid));
}
